
import { useMemo } from 'react';
import { EtsyOrder } from '@/types/etsy';

interface Expense {
  id: string;
  description: string;
  amount: number;
  category: string;
  expense_date: string;
  notes?: string;
}

interface AnalyticsData {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
  averageOrderValue: number;
  totalOrders: number;
  primaryCurrency: string;
  monthlyData: {
    month: string;
    sales: number;
    orders: number;
    expenses: number;
    profit: number;
  }[];
  expensesByCategory: {
    category: string;
    amount: number;
  }[];
  statusData: {
    name: string;
    value: number;
    color: string;
  }[];
  revenueGrowth: {
    month: string;
    revenue: number;
    expenses: number;
    profit: number;
  }[];
}

export const useReportsAnalytics = (orders: EtsyOrder[], expenses: Expense[]) => {
  const analytics = useMemo((): AnalyticsData => {
    // Get primary currency
    const currencyCount = orders.reduce((acc, order) => {
      const currency = order.currencyCode || 'USD';
      acc[currency] = (acc[currency] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const primaryCurrency = Object.entries(currencyCount)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'USD';

    // Calculate total revenue and expenses
    const totalRevenue = orders.reduce((sum, order) => {
      if (order.totalAmount && order.currencyCode === primaryCurrency) {
        return sum + order.totalAmount;
      }
      return sum;
    }, 0);

    const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    const netProfit = totalRevenue - totalExpenses;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    const averageOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;

    // Group orders by month
    const monthlyOrderData = orders.reduce((acc, order) => {
      const date = new Date(order.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!acc[monthKey]) {
        acc[monthKey] = {
          month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          sales: 0,
          orders: 0,
          revenue: 0
        };
      }
      
      acc[monthKey].orders += 1;
      acc[monthKey].sales += 1; // Assuming 1 order = 1 sale for now
      
      if (order.totalAmount && order.currencyCode === primaryCurrency) {
        acc[monthKey].revenue += order.totalAmount;
      }
      
      return acc;
    }, {} as Record<string, any>);

    // Group expenses by month
    const monthlyExpenseData = expenses.reduce((acc, expense) => {
      const date = new Date(expense.expense_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!acc[monthKey]) {
        acc[monthKey] = 0;
      }
      
      acc[monthKey] += expense.amount;
      return acc;
    }, {} as Record<string, number>);

    // Combine monthly data
    const allMonths = new Set([
      ...Object.keys(monthlyOrderData),
      ...Object.keys(monthlyExpenseData)
    ]);

    const monthlyData = Array.from(allMonths)
      .sort()
      .map(monthKey => {
        const orderData = monthlyOrderData[monthKey] || { month: '', sales: 0, orders: 0, revenue: 0 };
        const expenses = monthlyExpenseData[monthKey] || 0;
        
        return {
          month: orderData.month || new Date(monthKey + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          sales: orderData.sales,
          orders: orderData.orders,
          expenses,
          profit: orderData.revenue - expenses
        };
      });

    // Revenue growth data (same as monthly data for consistency)
    const revenueGrowth = monthlyData.map(item => ({
      month: item.month,
      revenue: item.sales * (averageOrderValue || 50), // Use calculated AOV or fallback
      expenses: item.expenses,
      profit: item.profit
    }));

    // Expenses by category
    const expensesByCategory = expenses.reduce((acc, expense) => {
      const existing = acc.find(item => item.category === expense.category);
      if (existing) {
        existing.amount += expense.amount;
      } else {
        acc.push({ category: expense.category, amount: expense.amount });
      }
      return acc;
    }, [] as { category: string; amount: number; }[]);

    // Status data
    const statusCounts = orders.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const statusColors = {
      'New': '#3b82f6',
      'Processing': '#f59e0b',
      'Shipped': '#10b981',
      'Delivered': '#6b7280',
      'Completed': '#059669',
      'Cancelled': '#ef4444',
      'Refunded': '#ec4899'
    };

    const statusData = Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value,
      color: statusColors[name as keyof typeof statusColors] || '#6b7280'
    }));

    return {
      totalRevenue,
      totalExpenses,
      netProfit,
      profitMargin,
      averageOrderValue,
      totalOrders: orders.length,
      primaryCurrency,
      monthlyData,
      expensesByCategory,
      statusData,
      revenueGrowth
    };
  }, [orders, expenses]);

  const formatPrice = (amount: number, currency?: string) => {
    const finalCurrency = currency || analytics.primaryCurrency;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: finalCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  };

  return {
    analytics,
    formatPrice
  };
};
